import React, { useMemo } from 'react';
import { IFileDataWithSharing } from '../file-manager-details-sheet';
import { SharedUser } from '../../utils/file-manager';
import { useTranslation } from 'react-i18next';
import { useDocumentSharedUsersQuery } from '../../hooks/use-document-manager';
import { useGetGroupsByIds } from 'features/iam/hooks/use-iam';

interface FileModalsProps {
  isRenameModalOpen: boolean;
  onRenameModalClose: () => void;
  onRenameConfirm: (newName: string) => void;
  fileToRename: IFileDataWithSharing | null;

  isShareModalOpen: boolean;
  onShareModalClose: () => void;
  onShareConfirm: (usersToShare: SharedUser[], usersToUnshare: SharedUser[], selectedGroups: any, groupsUnshare: any, isGroupShare: boolean, permissions: { [key: string]: string }) => void;
  fileToShare: IFileDataWithSharing | null;

  RenameModalComponent: React.ComponentType<any>;
  ShareModalComponent: React.ComponentType<any>;
  SharedWithTenantModalComponent?: React.ComponentType<any>;

  isSharedWithTenantModalOpen?: boolean;
  onSharedWithTenantModalClose?: () => void;
  onSharedWithTenantConfirm?: () => void;
}

interface GetGroupsByIdsResponse {
  response: any;
}

export const FileModals: React.FC<FileModalsProps> = ({
  isRenameModalOpen,
  onRenameModalClose,
  onRenameConfirm,
  fileToRename,
  isShareModalOpen,
  onShareModalClose,
  onShareConfirm,
  isSharedWithTenantModalOpen,
  onSharedWithTenantModalClose,
  onSharedWithTenantConfirm,
  fileToShare,
  RenameModalComponent,
  ShareModalComponent,
  SharedWithTenantModalComponent,
}) => {
  const { t } = useTranslation();

  const userIds = useMemo(() => fileToShare?.sharedWith?.filter(u => u.type === 'USER').map(u => u.id) || [], [fileToShare]);
  const groupIds = useMemo(() => fileToShare?.sharedWith?.filter(u => u.type === 'USER_GROUP').map(g => g.id) || [], [fileToShare]);
  let { data: currentSharedUsers = [] } = useDocumentSharedUsersQuery({
    page: 0,
    pageSize: 50,
    filter: { userIds },
    enabled: isShareModalOpen && !!userIds.length,
  });

  const { data: currentSharedGroups } = useGetGroupsByIds({ groupIds }) as { data: GetGroupsByIdsResponse };

  if (userIds.length === 0) {
    currentSharedUsers = [];
  }
  const currentSharedGroupsResponse = currentSharedGroups?.response || [];

  return (
    <>
      <RenameModalComponent
        isOpen={isRenameModalOpen}
        onClose={onRenameModalClose}
        onConfirm={onRenameConfirm}
        file={fileToRename}
      />

      <ShareModalComponent
        isOpen={isShareModalOpen}
        onClose={onShareModalClose}
        onConfirm={onShareConfirm}
        currentSharedUsers={currentSharedUsers}
        currentSharedGroups={currentSharedGroupsResponse}
      />

      {SharedWithTenantModalComponent && <SharedWithTenantModalComponent
        open={isSharedWithTenantModalOpen}
        onOpenChange={onSharedWithTenantModalClose}
        title={t('SHARED_WITH_TENANT_TITLE')}
        description={`${t('CONFIRM_ACTION_DESCRIPTION')}`}
        onConfirm={onSharedWithTenantConfirm}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />}
    </>
  );
};
